import { documentWord } from "../documentWord";

// What each role may do on the Management screen.
//
// **Data, not a condition scattered through the component.** The screen used to
// be a flat tab strip whose every button carried its own `isAdmin &&` or
// `canManageAGroup &&`, which had two costs: an account holding several roles
// could not tell which power came from which role, and there was nothing to
// stop a new tab being added under the wrong gate — a question nobody asks
// while adding a tab.
//
// `roles.js` remains the authority on what a role IS; this says what a role
// SHOWS. Both are mirrors of the backend — the server refuses what it refuses
// regardless, and a control offered here that the API rejects is a bug in this
// file, not a way in.

/** Sections, in the order they appear inside a role's card.
 *
 * **A label says what is inside**, and on 2026-09-09 four of them did not.
 * "Notifications" held the SMTP connection and four message templates, none of
 * which is a notification anybody receives; "Settings" sat on a screen where
 * three other sections are also settings; and the pair "Groups" / "Group
 * management" gave no clue which one makes a new group. A name that describes
 * something other than its contents is what put the backup card inside
 * Automation in the first place.
 *
 * **The keys did not change with them.** `key` is what `renderSection` switches
 * on and what every test addresses; changing a key and a label together turns a
 * one-line rename into a hunt.
 */
export const SECTIONS = {
  users:         { key: "users",         label: "Users" },
  // One group's own details, prices, documents and storage.
  //
  // **Admin AND group admin**, and scoped by the card exactly as `quotations`
  // below is: the group-admin card of an account that is also an admin offers
  // the groups that account administers and nothing wider.
  //
  // One difference from every other entry, and it is worth stating because the
  // sentence "this only decides what is shown" does not cover it: this card
  // also decides what may be CHANGED. `GroupManagementTab` is handed its card's
  // role and derives `canEdit` from it, so in the group-admin card an admin
  // reads a group they do not administer rather than editing it. The server
  // would allow the edit — `_require_group_admin` short-cuts for a global admin
  // — so this is the UI declining to offer a power under a heading that does
  // not carry it, never a permission granted here. A card may narrow; what it
  // may never do is widen.
  groupManage:   { key: "groupManage",   label: "Facility settings" },
  groups:        { key: "groups",        label: "Groups: create and remove" },
  notifications: { key: "notifications", label: "E-mail and templates" },
  // How every account gets in: the Keycloak connection and whether a password
  // still works at all. Added 2026-09-24.
  //
  // **Admin only**, and it is the one section whose misuse locks everybody
  // out, admins included — password sign-in switched off (`local_login:
  // off`; the setting is `on | off` since 2026-09-28) with a Keycloak that
  // does not answer leaves the break-glass command on the cluster as the only way back.
  // The server refuses the route to anybody else; this only decides what is
  // shown.
  signIn:        { key: "signIn",        label: "Sign-in" },
  // What the sign-in page offers to read — manuals, tutorials (2026-11-04).
  // **Admin only**: the list is public once saved, on the page every visitor
  // sees first. It replaced PORTAL_GUIDE_URL.
  loginLinks:    { key: "loginLinks",    label: "Sign-in page links" },
  announce:      { key: "announce",      label: "Announcements" },
  automation:    { key: "automation",    label: "Automation" },
  // Its own section since 2026-09-09. It sat at the tail of Automation because
  // a card had to go somewhere, and the two have nothing to do with each other:
  // one is scheduled work and the delivery queue, the other takes a copy of the
  // whole database. The group-scope half already had its own collapsible.
  //
  // **Admin only.** The full-scope dump carries every project, customer, price
  // and log entry — `BackupCard`'s own docstring says why its confirmation is
  // not ceremony.
  backup:        { key: "backup",        label: "Backup and import" },
  settings:      { key: "settings",      label: "Portal settings" },
  // Every published document in every group, and the record of one of them.
  // Admin only, because the record it exports carries other people's names,
  // addresses and IPs — the server refuses it to anybody else through
  // `rbac.is_global_admin`, and this only decides what is shown.
  documents:     { key: "documents",     label: "Documents across groups" },
  // Every offer the facility has made, in every state, as one CSV. Added
  // 2026-09-14, after the facility went looking for it on *Documents across
  // groups* and found nothing quotation-shaped there.
  //
  // **Admin AND group admin**, unlike its neighbour above. The document export
  // hands over other people's names, addresses and IP addresses, which is why
  // that one is the global admin's alone; this hands over a group's own
  // commercial figures, and the person who runs the group is exactly who that
  // belongs to. The server scopes it — `_stats_scope` answers a group admin
  // with a group they manage whatever they ask for, and refuses an operator
  // outright — so this only decides what is shown.
  //
  // What is shown is scoped by the card, not by the account: `renderSection`
  // hands the panel the role whose card it is rendering, so the group-admin
  // card of an account that is also an admin offers that account's administered
  // groups and nothing wider. Until 2026-09-15 it read the page-level `isAdmin`
  // and offered the whole facility under a Group admin heading — a card
  // widening what a role can do, which is the one thing this file forbids.
  //
  // "The server scopes it, so this only decides what is shown" still describes
  // this entry after that change, and it is the reason the export itself needed
  // no server-side work: `_stats_scope` was already narrowing. `groupManage`
  // above is where the same fix reached further than what is shown, and its
  // note says why.
  //
  // `label` is the name under the default setting; a screen reads it through
  // `sectionLabel`, which names the export for the CURRENT *Customer
  // documents* setting (2026-11-03).
  quotations:    { key: "quotations",    label: "Quotations export",
                   labelFor: kind => `${documentWord(kind, { plural: true, capital: true })} export` },
};

/** What a section is called on screen, for the current *Customer documents*
 *  setting. Every other section has one name. */
export function sectionLabel(section, kind) {
  return section.labelFor ? section.labelFor(kind) : section.label;
}

// One entry per role, listing only what that role may reach.
//
// The overlap is the point rather than duplication to factor out: `users`
// appears under three roles because all three may manage accounts, and each
// does so within different limits. Those limits are NOT expressed here — they
// come from `grantableRoles(me, group)`, so an operator's card offers only the
// roles an operator can actually grant. A card must never widen what a role can
// do; it only decides what is shown.
export const ROLE_SECTIONS = {
  admin: [
    // Group management is here as well as on the group-admin card because a
    // global admin holds standing in every group — `isGlobalAdmin` short-cuts
    // `rank_in_group` server-side, and the old page gave admins this tab for
    // the same reason. Dropping it would have been a silent regression: an
    // account holding only `admin` would have lost every facility setting.
    SECTIONS.users, SECTIONS.groups, SECTIONS.groupManage, SECTIONS.documents,
    SECTIONS.quotations, SECTIONS.notifications, SECTIONS.signIn, SECTIONS.loginLinks,
    SECTIONS.announce, SECTIONS.automation, SECTIONS.backup, SECTIONS.settings,
  ],
  group_admin: [SECTIONS.users, SECTIONS.groupManage, SECTIONS.quotations],
  operator:    [SECTIONS.users],
  // A plain user manages nothing. The entry exists so that "which sections does
  // this role have" is answerable for every role rather than throwing, and so
  // the card can say plainly that there is nothing here — which is more useful
  // than an account wondering where its options went.
  user: [],
};

export function sectionsForRole(role) {
  return ROLE_SECTIONS[role] || [];
}

/** The roles this account holds that actually show something, strongest first.
 *
 * Deduplicated: an operator in two groups holds two grants of one role and gets
 * one card, not two. Which groups a role covers is the card's business, not the
 * card list's. */
export function manageableRoles(grants) {
  const order = ["admin", "group_admin", "operator", "user"];
  const held = new Set((grants || []).map(g => g.role));
  return order.filter(r => held.has(r) && sectionsForRole(r).length > 0);
}
